<!-- eslint-disable comma-dangle arrow-body-style no-plusplus -->
<template>
  <AppEventContainer>
    <AppSectionHeader>Event Registration</AppSectionHeader>

    <!-- Event Header -->
    <EventListItem v-if="pokerRun" :show-details="false" :poker-run-event="pokerRun" />

    <!-- Registration Details Step -->
    <div v-show="currentStep === stepDetails">
      <!-- Header for items to purchase -->
      <div class="flex border-b gap-4 border-border-light pb-2 mb-2">
        <div class="flex justify-between flex-1">
          <div class="text-normal-emphasis font-medium text-lg">Item</div>
        </div>
        <div class="w-[60px] text-right text-normal-emphasis font-medium text-lg">Price</div>
      </div>

      <!-- For registered users: also show extra card selection -->
      <template v-if="isUserRegistered && pokerRun?.eventSettings.enableExtraCard && canPurchaseExtraCard">
        <div class="mb-2">
          <AssignExtraCards :event-participants="userParticipants" :quantity="userParticipants.length"
            :extra-card-price="extraCardLineItem?.amount || 0" :include-extra-card="includeExtraCard"
            @assignment-updated="handleAssignmentUpdated" @update:extra-card-count="updateExtraCardCount" />
          <p class="text-sm text-normal-reduced">
            You're already registered for this event. You can purchase extra cards for your existing hands.
          </p>
        </div>
      </template>
      <!-- Always show the RegistrationSelector -->
      <div v-if="pokerRun?.eventSettings.registrationStatus === 'open'" class="mb-2">
        <RegistrationSelector :poker-run="pokerRun" @update:registration-items="updateRegistrationItems" />
      </div>

      <div class="mb-2">
        <OtherItemSelector :poker-run="pokerRun" @update:selected-items="updateOtherItems" />
      </div>

      <div class="text-normal-emphasis font-bold flex justify-between p-2  border-t border-border-light">
        <div>Order subtotal</div>
        <div>{{ formatCurrency(orderSubtotal ?? 0, false) }}</div>
      </div>

      <div class="pb-4 content-centered mt-4">
        <div class="is-size-4 has-text-semibold mt-2" v-if="pokerRun?.eventSettings.registrationStatus === 'closed'">
          The event registration is closed.
        </div>
        <div class="is-size-4 has-text-semibold" v-else-if="maxCapacity">
          The event is full and is no longer accepting registrations.
        </div>
        <div class="is-size-4 has-text-semibold"
          v-else-if="transactionCharge?.exceedsPreSaleLimit || (transactionCharge?.amount || 0) + preSaleVolume > 60000">
          This event is unable to accept registrations at this time. Please try again later.
        </div>
      </div>

    </div>

    <EventCost v-if="currentStep === stepSummary" class="pb-2" :line-items="lineItemsWithServiceFee"
      :total-amount="transactionCharge?.subtotal || 0"></EventCost>

    <div class="py-2 content-centered" v-show="currentStep === stepPayment" v-if="clientSecret">
      <Payment ref="refpayment" :client-secret="clientSecret" :line-items="lineItems"
        :connected-account-id="connectedAccount" @error="paymentError" @success="handlePaymentSuccess"
        @initailized="paymentInitalized"></Payment>
    </div>

    <!-- Next Button -->
    <div class="flex gap-2">
      <AppButton v-if="currentStep !== stepDetails" title="Previous" class="w-full" @click="cancelCheckout"
        iconLeft="fa-angle-left">
      </AppButton>
      <AppButton :disabled="lineItems.length === 0 && extraCardCount === 0" :is-loading="isBusy" :title="nextButtonName"
        class="w-full" @click="customContinueCheckout" iconRight="fa-angle-right">

      </AppButton>
    </div>
  </AppEventContainer>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as Sentry from "@sentry/vue";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { Item } from "./models/Item";
import { EventParticipant } from "@models/EventParticipant";
import Constants from "@/models/Constants";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import EventParticipantDataService from "@/services/EventParticipantDataService";
import { useUserProfileStore } from "@/store/UserProfile";
import { UserTransaction } from "@models/UserTransaction";
import useCheckout from "@/modules/useCheckout";
import useDate from "@/modules/useDate";
import ConnectedAccountDataService from "@/services/ConnectedAccountDataService";
import { SelectOption } from '@/models/SelectOption';
import AppSectionHeader from "@/components/AppSectionHeader.vue";
import EventListItem from "@/components/EventListItem.vue";
import EventCost from "@/components/EventCost.vue";
import Payment from "@/components/Payment.vue";
import RegistrationSelector from "@/components/RegistrationSelector.vue";
import OtherItemSelector from "@/components/OtherItemSelector.vue";
import AssignExtraCards from "@/components/AssignExtraCards.vue";
import useEvent from "@/modules/useEvent";
import { useNotificationInjection } from "@/composables/useNotification";
import useLocalization from "@/modules/useLocalization";

interface RegistrationItem {
  item: Item;
  count: number;
  countWithExtraCard: number;
}

const { isFreeEvent } = useEvent();
const { formatISODate } = useDate();
const notify = useNotificationInjection();

const { formatCurrency } = useLocalization();

const route = useRoute();
const router = useRouter();
const userProfileStore = useUserProfileStore();
const eventId = ref("");
const pokerRun = ref<PokerRunEvent>();
const registrationItems = ref<RegistrationItem[]>([]);
const lineItems = ref<Item[]>([]);
const userParticipants = ref<EventParticipant[]>([]);
const isUserRegistered = ref(false);
const canPurchaseExtraCard = ref(true)
const extraCardCount = ref(0);
const assignedParticipantIds = ref<string[]>([]);

const refpayment = ref(null);
const includeExtraCard = ref(false);
const today = new Date();
today.setHours(0, 0, 0, 0);

const extraCardLineItem = computed(() =>
  pokerRun.value?.lineItems.find(x => x.itemType === Constants.EXTRA_CARD_TYPE)
);

const registrationOptions: SelectOption[] = [
  { id: 0, name: '0' },
  { id: 1, name: '1' },
  { id: 2, name: '2' },
  { id: 3, name: '3' },
  { id: 4, name: '4' },
  { id: 5, name: '5' },
  { id: 6, name: '6' },
  { id: 7, name: '7' },
  { id: 8, name: '8' },
  { id: 9, name: '9' },
  { id: 10, name: '10' }
];

const {
  stepDetails,
  stepSummary,
  stepPayment,
  transactionId,
  currentStep,
  isBusy,
  paymentIntentId,
  clientSecret,
  transactionCharge,
  defaultConnectedAccount,
  continueCheckout,
  cancelCheckout,
  paymentSuccess,
  paymentError,
  paymentInitalized,
} = useCheckout(
  "registration",
  route.params.id as string,
  refpayment,
  () => {
    // If we have extra cards to assign to existing participants
    if (isUserRegistered.value && extraCardCount.value > 0 && assignedParticipantIds.value.length > 0) {
      // Assign participant IDs to the extra card line items
      const items = [...lineItems.value];

      // Find all extra card items
      const extraCardItems = items.filter(item =>
        item.itemType === Constants.EXTRA_CARD_TYPE ||
        (item.metadata && item.metadata.isExtraCard));

      // Assign participant IDs to extra card items
      for (let i = 0; i < extraCardItems.length; i++) {
        if (i < assignedParticipantIds.value.length) {
          extraCardItems[i].participantId = assignedParticipantIds.value[i];
        }
      }

      return items;
    }

    return lineItems.value;
  },
  () => {
    if (currentStep.value === stepDetails) {
      return stepSummary;
    } else if (
      currentStep.value === stepSummary &&
      (transactionCharge.value?.subtotal === 0 || !transactionCharge.value?.subtotal)
    ) {
      return stepDetails;
    } else if (currentStep.value === stepSummary) {
      return stepPayment;
    } else {
      return stepDetails;
    }
  },
  route.query.fundraiser as string
);

const isZeroDollarEvent = computed(() => {
  if (!pokerRun.value) return false;
  return pokerRun.value.lineItems.reduce((prev, curr) => prev + curr.amount, 0) === 0;
});

const createLineItem = (itemType): Item[] => {
  if (!pokerRun.value) return [];
  const requiredItems = pokerRun.value?.lineItems
    .filter((x) => x.itemType === itemType)
    .map((item) => {
      return { ...item } as Item;
    });
  return requiredItems;
};

const lineItemsWithServiceFee = computed<Item[]>(() => {
  if (!pokerRun.value) return [];
  const fees: Item[] = [...lineItems.value];

  if (
    transactionCharge.value &&
    !transactionCharge.value.isServiceFeeIncluded &&
    transactionCharge.value.amount > 0
  ) {
    fees.push(({
      name: Constants.SERVICE_FEE,
      amount: transactionCharge.value.serviceFee,
      itemId: Constants.SERVICE_FEE,
      itemType: Constants.SERVICE_FEE,
    } as unknown) as Item);
  }
  return fees;
});

const removeAssignedExtraCards = () => {
  lineItems.value = lineItems.value.filter((item: Item) => item.itemType !== Constants.EXTRA_CARD_TYPE && item.participantId);
};

const addExtraCardLineItems = (participantIds: string[]) => {
  if (!extraCardLineItem.value) return;

  participantIds.forEach((participantId) => {
    lineItems.value.push({
      ...extraCardLineItem.value,
      participantId,
    });
  });
};

const nextButtonName = computed(() => {
  if (currentStep.value === stepSummary && pokerRun.value) {
    return isFreeEvent(pokerRun.value as PokerRunEvent) ? "Register" : "Check out";
  } else {
    return "Next";
  }
});

const preSaleVolume = computed(() => {
  const preSaleVolume = pokerRun.value?._internal.preSaleVolume ?? 0;
  return preSaleVolume;
});

const connectedAccount = computed(() => {
  if (pokerRun.value?._internal.connectedAccountDetails?.stripeType === 'legacy') {
    return null;
  }

  return pokerRun.value?._internal.connectedAccountDetails?.onboarded ?
    pokerRun.value?._internal?.connectedAccountDetails.id :
    defaultConnectedAccount.value;
});

const maxCapacity = computed(() => {
  const hasCap =
    pokerRun.value &&
    pokerRun.value?.eventSettings.registrationCap &&
    pokerRun.value?._internal?.registrationCount;

  return hasCap
    ? (pokerRun.value?._internal?.registrationCount ?? 0) >=
    (pokerRun.value?.eventSettings.registrationCap ?? 0)
    : false;
});

const canRegister = computed(
  () =>
    pokerRun.value?.status === "active" &&
    pokerRun.value?.eventSettings.registrationStartDate.substring(0, 10) <=
    formatISODate(new Date()) &&
    pokerRun.value?.eventDate.substring(0, 10) >= formatISODate(today) &&
    !maxCapacity.value &&
    pokerRun.value?.eventSettings.registrationStatus !== "closed" &&
    !(transactionCharge.value?.exceedsPreSaleLimit) &&
    !((transactionCharge.value?.amount || 0) + preSaleVolume.value > 60000)
);

const updateRegistrationItems = (items: RegistrationItem[]) => {
  const selectedItems = items.filter(x => x.count > 0 || x.countWithExtraCard > 0);
  const otherItems = registrationItems.value.filter(x => x.item.itemType !== Constants.FEE_TYPE);
  registrationItems.value = [...selectedItems, ...otherItems];;
  calcLineItems();
};

const updateOtherItems = (items: RegistrationItem[]) => {
  const selectedItems = items.filter(x => x.count > 0);
  const feeItems = registrationItems.value.filter(x => x.item.itemType === Constants.FEE_TYPE);
  registrationItems.value = [...feeItems, ...selectedItems];
  calcLineItems();
};

const orderSubtotal = computed(() => {
  const extraCardFee = pokerRun.value?.lineItems.find(x => x.itemType === Constants.EXTRA_CARD_TYPE);
  const extraCardFeeAmount = extraCardFee?.amount ?? 0;
  const itemAmounts = registrationItems.value.map(x => x.item.amount * (x.count ?? 0) + (x.item.amount + extraCardFeeAmount) * (x.countWithExtraCard ?? 0));
  const itemSubtotal = itemAmounts.reduce((c, n) => c + n, 0);
  if (extraCardCount.value > 0) {
    return itemSubtotal + extraCardCount.value * extraCardFeeAmount;
  }
  return itemSubtotal;
})

const updateExtraCardCount = (count: number) => {
  extraCardCount.value = count;
};

const calcLineItems = () => {
  const newLineItems: Item[] = [];

  // For registered users purchasing only extra cards
  if (isUserRegistered.value && extraCardLineItem.value && extraCardCount.value > 0) {
    // Add extra cards based on the number selected in AssignExtraCards
    for (let i = 0; i < extraCardCount.value; i++) {
      // Create a copy of the extra card line item
      const extraCard = { ...extraCardLineItem.value };
      newLineItems.push(extraCard);
    }
  }
  // Regular registration flow
  registrationItems.value.forEach(registration => {
    // Add regular registrations
    for (let i = 0; i < registration.count; i++) {
      newLineItems.push({ ...registration.item });
    }

    // Add registrations with extra cards
    if (registration.countWithExtraCard) {
      for (let i = 0; i < registration.countWithExtraCard; i++) {
        newLineItems.push({ ...registration.item });
        if (extraCardLineItem.value) {
          newLineItems.push({ ...extraCardLineItem.value });
        }
      }
    }
  });


  lineItems.value = newLineItems;
  console.log('Line items:', lineItems.value);
};

watch(includeExtraCard, async (newVal: boolean, oldVal: boolean) => {
  if (newVal !== oldVal) {
    calcLineItems();
  }
});

// watch(() => registrationItems.value, () => {
//   calcLineItems();
// }, { deep: true });

const initializeRegistrationItems = () => {
  if (!pokerRun.value) return;

  // const feeItems = pokerRun.value.lineItems.filter(x => x.itemType === Constants.FEE_TYPE);
  // registrationItems.value = feeItems.map(item => ({
  //   item,
  //   count: 0,
  //   countWithExtraCard: 0
  // }));
};

watch(() => pokerRun.value, (newVal) => {
  if (newVal) {
    initializeRegistrationItems();
  }
}, { immediate: true });

const handleAssignmentUpdated = (participantIds: string[]) => {
  assignedParticipantIds.value = participantIds;
};

const initialize = async () => {
  UserTransactionDataService.warmup();
  ConnectedAccountDataService.warmup();
  const pokerRunEvent: PokerRunEvent = await PokerRunEventDataService.get(
    route.params.id as string
  );

  pokerRun.value = pokerRunEvent;

  // Check if includeExtraCard query parameter is set to true
  if (route.query.includeExtraCard === 'true') {
    includeExtraCard.value = true;
  }

  // Check if user is already registered for this event
  try {
    const participants = await EventParticipantDataService.get(
      route.params.id as string,
      userProfileStore.profile.id
    );

    userParticipants.value = participants;
    isUserRegistered.value = participants.length > 0;
    canPurchaseExtraCard.value = !participants.some(x => x.stops.some(s => s.checkedIn && s.order > 2))

    // // If user is registered, initialize registration items for extra cards only
    // if (isUserRegistered.value && pokerRunEvent.eventSettings.enableExtraCard) {
    //   // Create a registration item for extra cards only
    //   // if (extraCardLineItem.value) {
    //   //   registrationItems.value = [{
    //   //     item: extraCardLineItem.value,
    //   //     count: 0,
    //   //     countWithExtraCard: 0
    //   //   }];
    //   // }
    // } else {
    //   initializeRegistrationItems();
    // }
  } catch (error) {
    console.error("Error checking registration status:", error);
    initializeRegistrationItems();
  }

  calcLineItems();
  Sentry.setContext("event", {
    eventId: pokerRun.value.id,
    eventName: pokerRun.value.name,
  });

  const transactions: UserTransaction[] = await UserTransactionDataService.getUserTransactions(
    userProfileStore.profile.id,
    route.params.id as string
  );

  const transaction = transactions.find(
    (x) => x.status === "pending"
  );

  transactionId.value = transaction?.transactionDetails.transactionId as string;
  paymentIntentId.value = transaction?.transactionDetails.paymentIntentId as string;
};

// Custom payment success handler for extra card purchases
const handlePaymentSuccess = async (paymentIntent: any) => {
  try {
    // If we have extra cards to assign, log the information for debugging
    if (assignedParticipantIds.value.length > 0) {
      console.log('Extra card assignments:', {
        eventId: route.params.id,
        transactionId: transactionId.value,
        participantIds: assignedParticipantIds.value
      });

      notify.success(`Your extra card purchase was successful. The cards will be assigned to your selected hands.`);
    }
  } catch (error) {
    console.error('Error processing extra card purchase:', error);
  }

  // Navigate to the registered event page
  router.push({
    name: "PaymentConfirmation",
    params: { id: route.params.id as string, transactionId: transactionId.value },
  });
};

// Custom function to handle checkout with extra card assignment
const customContinueCheckout = async (event: any) => {
  // If we're on the details step and the user is registered with extra cards
  if (currentStep.value === stepDetails && isUserRegistered.value && extraCardCount.value > 0) {
    removeAssignedExtraCards();
    addExtraCardLineItems(assignedParticipantIds.value);
  }

  // Call the original continueCheckout function
  await continueCheckout(event);
};

if (eventId.value && eventId.value !== route.params.id) {
  eventId.value = route.params.id as string;
} else if (!eventId.value) {
  eventId.value = route.params.id as string;
}

initialize();
</script>
